// Static content for RoboInsci × Gakuen Technologies
export const PROGRAMS = [
  {
    slug: "robotics",
    code: "PRG/01",
    title: "Robotics Engineering",
    blurb:
      "Build, code, and command real machines. From line-followers to humanoid arms — students engineer working robots from scratch.",
    duration: "8 — 24 weeks",
    level: "Grade 5 — College",
    outcomes: [
      "Mechanical assembly & sensor integration",
      "Embedded C / MicroPython / Arduino",
      "Autonomous navigation & control loops",
      "Live competition projects",
    ],
    icon: "Cpu",
  },
  {
    slug: "ai",
    code: "PRG/02",
    title: "Generative AI and Tools",
    blurb:
      "Master the latest in Generative AI. From prompt engineering to deploying automated AI agents, students build real-world applications using current AI tools.",
    duration: "10 — 16 weeks",
    level: "Grade 8 — College",
    outcomes: [
      "Automated AI & current AI tools",
      "Web & App development integration",
      "Product & Application development",
      "GenAI workflow automation",
    ],
    icon: "Brain",
  },
  {
    slug: "iot",
    code: "PRG/03",
    title: "Internet of Things",
    blurb:
      "Wire the physical world into the cloud. ESP32, sensors, MQTT, dashboards — build smart classrooms, smart farms, smart cities.",
    duration: "6 — 12 weeks",
    level: "Grade 6 — College",
    outcomes: [
      "ESP32 / Raspberry Pi firmware",
      "Sensor fusion & calibration",
      "MQTT, REST & WebSocket pipelines",
      "Realtime dashboards",
    ],
    icon: "Radio",
  },
  {
    slug: "app-dev",
    code: "PRG/04",
    title: "App & Web Development",
    blurb:
      "From an idea on a whiteboard to a published app. We teach product thinking alongside the code that ships it.",
    duration: "8 — 16 weeks",
    level: "Grade 7 — College",
    outcomes: [
      "React, REST APIs & databases",
      "UI/UX fundamentals",
      "Mobile-first deployment",
      "Live student-built apps",
    ],
    icon: "Code2",
  },
  {
    slug: "3d-printing",
    code: "PRG/05",
    title: "3D Design & Printing",
    blurb:
      "Translate imagination into tangible objects using state-of-the-art tech like Bambu Lab A1 Mini. Master multi-3D printing, resin printing, and Fusion 360.",
    duration: "4 — 10 weeks",
    level: "Grade 4 — College",
    outcomes: [
      "Fusion 360 & Bambu Studio",
      "Bambu Lab A1 Mini & Multi-3D Printing",
      "Resin Printing & SLA technology",
      "Functional prototypes & makers' fair",
    ],
    icon: "Box",
  },
];

export const PRODUCTS = [
  {
    sku: "RIK-200",
    category: "Robotics Kit",
    title: "InsciBot Starter Kit",
    description:
      "An entry-level robotics kit with 32 components, 6 sensors and a graphical IDE. Designed for grades 5–8 to teach robotics in 12 structured lessons.",
    specs: ["Arduino-compatible MCU", "6 sensors + 4 motors", "Block & C++ IDE", "12 lesson plans"],
    useCases: ["Classroom kits", "Robotics clubs", "Inter-school competitions"],
    image:
      "https://images.unsplash.com/photo-1562758778-e5638b5b6607?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
  },
  {
    sku: "AI-450",
    category: "AI Learning System",
    title: "Gakuen AI Lab",
    description:
      "A turn-key AI learning station with curated datasets, Jupyter environment and progressive curriculum from grade 8 to college.",
    specs: ["Pre-configured Jupyter Lab", "Curated datasets", "GPU-ready", "60+ lessons"],
    useCases: ["AI labs", "Research clubs", "College capstones"],
    image:
      "https://images.unsplash.com/photo-1637002722490-5f8ceed9774c?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
  },
  {
    sku: "IOT-310",
    category: "IoT Devkit",
    title: "Insci IoT Bench",
    description:
      "An ESP32-based teaching bench with 10 industrial-grade sensors, an MQTT broker and a live dashboard — wired and ready for students.",
    specs: ["ESP32 + LoRa option", "10 sensors", "Self-hosted MQTT", "Realtime dashboard"],
    useCases: ["Smart school projects", "Smart agri labs", "Hackathons"],
    image:
      "https://images.unsplash.com/photo-1675602487652-3a4d8cdada94?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
  },
  {
    sku: "3DP-120",
    category: "3D Printing Lab",
    title: "Insci Maker Bay",
    description:
      "A complete 3D printing lab kit — printers, filaments, slicing software and curriculum integrated for design-and-make classrooms.",
    specs: ["2× FDM printers", "10kg filament", "Slicing curriculum", "Maintenance training"],
    useCases: ["STEM labs", "Innovation rooms", "Maker spaces"],
    image:
      "https://images.unsplash.com/photo-1642969164999-979483e21601?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
  },
];

export const SERVICES = [
  {
    code: "SVC/01",
    title: "School Workshops",
    description:
      "Single-day to multi-week workshops delivered on-campus by certified mentors — hands-on, outcomes-driven, curriculum-aligned.",
    bullets: ["1 to 12 week tracks", "Live build + showcase", "Certificates & rubrics"],
  },
  {
    code: "SVC/02",
    title: "Lab Setup & Integration",
    description:
      "Turn-key robotics, AI, IoT and 3D-printing labs — design, supply, install, train. From layout to launch in under 30 days.",
    bullets: ["End-to-end procurement", "Faculty enablement", "AMC + remote support"],
  },
  {
    code: "SVC/03",
    title: "Institutional Programs",
    description:
      "Year-long curriculum integrated into the school timetable. Mentorship, assessment, parent reports and competition pipelines.",
    bullets: ["NEP-aligned tracks", "Quarterly reports", "Olympiad pipeline"],
  },
  {
    code: "SVC/04",
    title: "Mentorship & Capstone",
    description:
      "1-on-1 and small-group mentorship for project-based learners — from idea to working prototype, judged by industry mentors.",
    bullets: ["Industry mentors", "Demo days", "Patent / publication support"],
  },
];

export const STATS = [
  { value: "15+", label: "Years of mentorship" },
  { value: "3,000+", label: "Students mentored" },
  { value: "25+", label: "Tech projects delivered" },
  { value: "10+", label: "Startups supported" },
];

export const PARTNERS = [
  { name: "Sphoorthy Engineering College", note: "Center of Excellence", logo: "/assets/images/sphoorthy.png" },
  { name: "Guru Nanak University", note: "Incubation centre established", logo: "/assets/images/GNIT.png" },
  { name: "Oasis Schools", note: "Robotics & AI workshops", logo: "/assets/images/oasis.png" },
  { name: "Telangana Model School, Suryapet", note: "Institutional training", logo: "/assets/images/telangana model school.png" },
  { name: "GITAM University", note: "IUCEE IASF · ", logo: "/assets/images/gittam.png" },
  { name: "DRDO", note: "Application development ", logo: "/assets/images/drdo.png" },
];

export const INDUSTRY_EXPOSURE = [
  {
    title: "Real-world problem statements",
    body: "Students work on industry- and community-aligned challenges — not toy datasets.",
  },
  {
    title: "Hackathons & prototyping sprints",
    body: "Structured ideation, build sprints and pitch events that ship actual prototypes.",
  },
  {
    title: "Incubation & IIC pipeline",
    body: "Promising teams route into incubation centers and Institution Innovation Council activities.",
  },
  {
    title: "Industry & startup ecosystem",
    body: "Partner collaborations connect projects to real founders, engineers and product mentors.",
  },
];

export const TEAM = [
  {
    initials: "SV",
    name: "Sharath Vedala",
    title: "Director · Gakuen Technologies",
    period: "2019 — Present",
    summary:
      "Technology mentor with 15+ years in robotics, innovation and product development. Hands-on builder who has supported 10+ startups and delivered 25+ technology projects, including applications shipped to DRDO.",
    highlights: [
      "Ex R&D Engineer · Robolab Technologies",
      "Head · Center of Excellence, Sphoorthy Engineering College",
      "Established incubation centers including Guru Nanak University",
      "Mentored 10+ technology startups · Delivered 25+ projects",
    ],
    skills: ["Product Development", "Project Management", "Tech Innovation"],
    image: "/assets/images/sharath.jpg",
  },
  {
    initials: "LM",
    name: "Laxmi Nivas Morishetty",
    title: "AI Engineer · Programmer · Innovation Lead",
    period: "Co-mentor",
    summary:
      "Computer Science & Engineering student at Sphoorthy Engineering College, Hyderabad. AI tools specialist and programmer; founder of multiple innovation initiatives. Mentored 3,000+ students.",
    highlights: [
      "Founder — multiple innovation & technology initiatives",
      "Projects: VID (Virtual ID), Super App, eVault (location security)",
      "Winner — IUCEE 2026 (GITAM University)",
      "Student Convener — IIC · President — Creater Club, Sphoorthy Engineering College",

    ],
    skills: ["AI Tools", "Web Development", "Cybersecurity", "Software Development"],
    image: "/assets/images/laxminivas.jpg",
  },
];

export const TESTIMONIALS = [
  {
    quote:
      "Our students went from textbook theory to shipping live robotics projects in a single semester. RoboInsci changed our innovation culture.",
    name: "Faculty Lead",
    role: "Sphoorthy Engineering College",
  },
  {
    quote:
      "The Gakuen Technologies team set up our incubation cell end-to-end and continues to mentor every batch.",
    name: "Innovation Office",
    role: "Guru Nanak University",
  },
  {
    quote:
      "Hands-on, no-jargon, outcome-driven workshops. Our students built, tested and demoed working prototypes.",
    name: "Coordinator",
    role: "Telangana Model School, Suryapet",
  },
];

export const REQUIREMENT_TYPES = [
  { value: "roboinsci_program", label: "Book a RoboInsci Program" },
  { value: "lab_setup", label: "Setup Lab with Gakuen" },
  { value: "products", label: "Buy Products / Kits" },
  { value: "institutional", label: "Institutional Partnership" },
  { value: "demo", label: "Request a Demo" },
  { value: "other", label: "Something Else" },
];
